import api from './api';

// Interfaces
export interface HearingChecklistTemplate {
  id: number;
  order_index: number;
  title: string;
  description?: string;
  responsible_role: 'abogado' | 'auxiliar' | 'coordinador';
  is_active: boolean;
}

export interface HearingChecklistItem {
  id: number;
  hearing_id: number;
  template_id: number;
  is_completed: boolean;
  completed_at?: string;
  completed_by?: number;
  notes?: string;
  order_index: number;
  title: string;
  description?: string;
  responsible_role: string;
  completed_by_name?: string;
}

export interface HearingChecklistSummary {
  hearing_id: number;
  case_number: string;
  client_name: string;
  hearing_type: string;
  scheduled_date: string;
  scheduled_time: string;
  total_items: number;
  completed_items: number;
  completion_pct: number;
}

export interface HearingChecklistResponse {
  summary: HearingChecklistSummary;
  items: HearingChecklistItem[];
}

const hearingChecklistService = {
  // ==========================================
  // TEMPLATES
  // ==========================================
  
  async listTemplates(): Promise<HearingChecklistTemplate[]> {
    const response = await api.get('/hearing-checklists/templates');
    return response.data;
  },

  async getTemplate(id: number): Promise<HearingChecklistTemplate> {
    const response = await api.get(`/hearing-checklists/templates/${id}`);
    return response.data;
  },

  async createTemplate(data: Partial<HearingChecklistTemplate>): Promise<{ id: number }> {
    const response = await api.post('/hearing-checklists/templates', data);
    return response.data;
  },

  async updateTemplate(id: number, data: Partial<HearingChecklistTemplate>): Promise<void> {
    await api.patch(`/hearing-checklists/templates/${id}`, data);
  },

  async deleteTemplate(id: number): Promise<void> {
    await api.delete(`/hearing-checklists/templates/${id}`);
  },

  // ==========================================
  // CHECKLIST POR AUDIENCIA
  // ==========================================

  async getChecklistByHearing(hearingId: number): Promise<HearingChecklistResponse> {
    const response = await api.get(`/hearing-checklists/hearing/${hearingId}`);
    return response.data;
  },

  async toggleChecklistItem(
    hearingId: number, 
    templateId: number, 
    completed: boolean,
    notes?: string
  ): Promise<{ summary: HearingChecklistSummary }> {
    const response = await api.post(
      `/hearing-checklists/hearing/${hearingId}/item/${templateId}`,
      { completed, notes }
    );
    return response.data;
  },

  async updateChecklistItem(id: number, data: { is_completed?: boolean; notes?: string }): Promise<void> {
    await api.patch(`/hearing-checklists/item/${id}`, data);
  },

  async getChecklistSummary(hearingId: number): Promise<HearingChecklistSummary> {
    const response = await api.get(`/hearing-checklists/hearing/${hearingId}/summary`);
    return response.data;
  },

  // ==========================================
  // REPORTES
  // ==========================================

  async getUpcomingWithChecklist(days: number = 7): Promise<HearingChecklistSummary[]> {
    const response = await api.get('/hearing-checklists/upcoming', { params: { days } });
    return response.data;
  },

  async getIncompleteChecklists(): Promise<HearingChecklistSummary[]> {
    const response = await api.get('/hearing-checklists/incomplete');
    return response.data;
  }
};

export default hearingChecklistService;
